import AssistantComposer, { AssistantAttachments } from '../components/AssistantComposer';
import { useAssistantDraft } from '../lib/assistantDraft';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Skull, Send, Loader2, LifeBuoy, Plus, ShieldAlert, ThumbsUp, ThumbsDown, UserCheck, MessagesSquare, RefreshCw,
} from 'lucide-react';
import { assistantApi } from '../lib/api';
import RichText from '../components/RichText';
import ProposalCard from '../components/ProposalCard';
import CopyProposalCard from '../components/CopyProposalCard';

/*
 * A5a: the customer support assistant (V0 "Answer" tier - plans/ai-integration-plan.md §3).
 *
 * What the user needs to understand from this page, stated on it: the assistant EXPLAINS and
 * cites; it cannot change any setting. Every answer is grounded in the product's own generated
 * knowledge plus this account's data; "Report to support" hands the thread to a human as a
 * ticket. Gated server-side by UIAPI_ASSISTANT (default OFF); this page reports that state
 * honestly instead of an eternal spinner.
 *
 * A6 (feedback loop): every answer can be rated "Did this resolve it?" - a No (or a low score
 * with a comment) escalates server-side, deduped with every other customer asking the same
 * thing. When a human approves a reply it is DELIVERED into this conversation, rendered as
 * "Support team" and marked unread in the list - this dashboard is the only channel there is.
 * The rating call always answers {ok:true}; nothing about what happened next is shown, on purpose.
 *
 * Proposals: a reply can also carry a change the customer may accept - `proposal` (new values for
 * one slot) or `copy` (one slot's settings onto another), never both, and both already dry-run
 * validated server-side. Each renders as a card under the transcript.
 *
 * This page had NEITHER, which was a real defect and not just a missing feature: the server strips
 * the machine block out of the reply text before sending it, so with no card the answer promised a
 * settings change and then nothing whatsoever appeared. The bubble rendered its card and this page
 * did not, and because the EXPANDED bubble renders this page (see `embedded` below) rather than its
 * own corner transcript, expanding to read a proposal in comfort is exactly what made it disappear.
 *
 * So the card handling is shared BEHAVIOUR deliberately duplicated, not code that wants extracting:
 * the two surfaces own two different transcripts (a 380px corner panel there, the full-height card
 * here) and only the state machine around the card is common - set it from the reply, clear it when
 * the conversation changes, hide it while the next answer is in flight. Both surfaces must implement
 * it, because a customer must see the same offer wherever they happened to ask.
 *
 * Layout (Aurora, 2026-08-23): the page takes exactly the height the shell leaves it, so the
 * transcript scrolls inside the card and the composer stays put at the bottom - above the mobile
 * tab bar, which the shell pads for. Conversations live in a side panel from `sm` up and in a
 * sheet below that (the list was unreachable on phones before).
 */

/*
 * The alpha marker for the page header.
 *
 * The bubble carries its own badge in its own header, but the EXPANDED view renders this page
 * instead of that header - so without this, expanding the assistant made the warning disappear
 * exactly when the user got the roomier view they were most likely to trust.
 */
const ALPHA_TITLE = (
  <>
    Undead Assistant
    <span className="ml-2 inline-flex items-center rounded-md border border-warning/40 bg-warning/10 px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-warning">
      Alpha
    </span>
  </>
);
/* "it never changes anything" was true when this page could only ever show text. It stopped being
 * true the moment a proposal card with an Apply button could appear underneath it, so the promise
 * is now the one that actually holds in both cases: nothing is applied unless the customer presses
 * Apply. Worded so it still reads correctly on an account where no proposal ever arrives. */
const DESCRIPTION = 'Alpha - still being built, so check anything it tells you before acting on it. Answers questions about your slots and settings from the product\'s own knowledge and your account\'s real state. It explains and cites, and it never changes a setting without showing you the change and asking first.';

/* The shell's <main> pads px/py and, below lg, leaves room for the bottom tab bar
 * (Layout.js: py-5 sm:py-6, pb = bottombar + safe-bottom + 1.25rem, lg:pb-8). These subtract
 * exactly that so the chat fills the viewport without the page itself scrolling. A floor keeps
 * the composer usable on very short (landscape phone) viewports - the page scrolls instead. */
const PAGE_HEIGHT = cn(
  'min-h-[26rem]',
  'h-[calc(100dvh-var(--topbar-h)-var(--safe-top)-var(--bottombar-h)-var(--safe-bottom)-2.5rem)]',
  'sm:h-[calc(100dvh-var(--topbar-h)-var(--safe-top)-var(--bottombar-h)-var(--safe-bottom)-2.75rem)]',
  'lg:h-[calc(100dvh-var(--topbar-h)-var(--safe-top)-3.5rem)]'
);

function RatingWidget({ message, onRate, busy }) {
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState('');
  const [comment, setComment] = useState('');
  if (message.resolved === true) {
    return <div className="mt-1 text-xs text-success">Marked as resolved — thanks.</div>;
  }
  if (message.resolved === false && !open) {
    return <div className="mt-1 text-xs text-muted-foreground">Sent to support — a human will reply here.</div>;
  }
  return (
    <div className="mt-1.5 text-xs text-muted-foreground" data-testid={`rate-${message.id}`}>
      {!open && (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          Did this resolve it?
          <Button size="sm" variant="ghost" className="h-6 px-2 text-success hover:text-success" disabled={busy}
                  onClick={() => onRate(message.id, { resolved: true })}>
            <ThumbsUp className="h-3.5 w-3.5 mr-1" /> Yes
          </Button>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-warning hover:text-warning" disabled={busy}
                  onClick={() => setOpen(true)}>
            <ThumbsDown className="h-3.5 w-3.5 mr-1" /> No
          </Button>
        </span>
      )}
      {open && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <select value={score} onChange={(e) => setScore(e.target.value)}
                  aria-label="Score"
                  className="h-7 rounded-md border border-border bg-secondary/60 px-1.5 text-xs text-foreground">
            <option value="">score (optional)</option>
            {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <Input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500}
                 placeholder="what was missing? (optional)"
                 className="h-7 w-full min-w-0 text-xs bg-secondary/60 border-border placeholder:text-muted-foreground/70 xs:w-64" />
          <Button size="sm" className="h-7 bg-warning text-background hover:bg-warning/90" disabled={busy}
                  onClick={() => {
                    onRate(message.id, { resolved: false, rating: score || undefined, comment: comment || undefined });
                    setOpen(false);
                  }}>
            Send to support
          </Button>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => setOpen(false)}>cancel</Button>
        </div>
      )}
    </div>
  );
}

/* The conversation list: the side panel (sm+) and the phone sheet render the same thing.
 * Frozen while a send is pending so a reply cannot land in the wrong view. */
function ConversationList({ chats, chatId, busy, onOpen, onNew, mobile = false }) {
  return (
    <>
      <Button variant="outline" className="tap w-full justify-start rounded-full" onClick={onNew} disabled={busy}>
        <Plus className="h-4 w-4" /> New conversation
      </Button>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {chats.length === 0 && (
          <p className="px-3 py-2 text-xs text-muted-foreground">No conversations yet.</p>
        )}
        {chats.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onOpen(c.id)}
            disabled={busy}
            className={cn(
              'tap flex w-full min-w-0 items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors disabled:opacity-50',
              c.id === chatId ? 'bg-primary/15 text-primary ring-1 ring-primary/25' : 'text-foreground/90 hover:bg-secondary/70'
            )}
            data-testid={mobile ? `chat-m-${c.id}` : `chat-${c.id}`}
          >
            {c.unreadSupportAnswer && (
              <span className="h-2 w-2 shrink-0 rounded-full bg-success" title="A human answered" />
            )}
            <span className="min-w-0 truncate">{c.title || `conversation ${c.id}`}</span>
          </button>
        ))}
      </div>
    </>
  );
}

/**
 * @param embedded rendered inside the assistant bubble's expanded overlay rather than as a route.
 *   The ONLY differences are the two things that belong to a page and not to a panel: the height,
 *   which is derived from the viewport here but from the panel there, and the browser tab title,
 *   which still belongs to whatever page the user is actually standing on. Everything else is
 *   deliberately identical - the expanded bubble IS this page, not a copy of it, so the two cannot
 *   drift apart.
 */
export default function AssistantPage({ embedded = false }) {
  usePageTitle(embedded ? false : 'Undead Assistant');
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [draft, setDraft, images, setImages] = useAssistantDraft(user?.email);
  // Namespaced per account: a shared browser must not try to reopen the previous user's
  // conversation on every mount (review finding). Server-side ownership already blocks any
  // data leak; this stops the pointless 404 toast.
  //
  // The namespacing means the key carries an email address, and logging out does not remove it.
  // Keys belonging to OTHER accounts are swept by AssistantBubble, which Layout mounts on every
  // authenticated page - one sweep, in the component that is always there, rather than a second
  // copy of it here.
  const storeKey = user?.email ? `assistantChatId:${user.email}` : null;
  const [chatId, setChatId] = useState(null);

  // {id, role, content, fromSupport, resolved, rating}. `role` is 'user' | 'assistant' from the
  // server, plus the local-only 'error' a failed send pushes - which has no id, so nothing that
  // keys off one (the rating widget) picks it up.
  const [transcript, setTranscript] = useState([]);
  // Phone-only: the conversations sheet (the side panel is hidden below `sm`).
  const [listOpen, setListOpen] = useState(false);
  /*
   * The card the last reply offered, if it offered one. Kept beside the transcript and not on a
   * message row, because every send reloads the transcript wholesale and a card stored on a row
   * would be thrown away by that reload - and it is transient anyway: apply it, decline it, or ask
   * again. Two states rather than one tagged value, because they render two different cards; the
   * server sends at most one of the two keys, and both are written on every reply, so the pair
   * cannot drift into showing two offers at once.
   */
  const [proposal, setProposal] = useState(null);
  const [copy, setCopy] = useState(null);
  const bottomRef = useRef(null);
  // The chat a pending send belongs to ('new' before the server assigns an id). A reply is only
  // spliced into the visible transcript if the user has not switched conversations meanwhile -
  // otherwise it silently mixed two conversations in one view (review finding).
  const pendingForRef = useRef(null);
  // Mirror of chatId for use inside callbacks (see onSuccess) without reading state in an updater.
  const chatIdRef = useRef(null);

  // Polled with the chat list: a human answer can land while the page is open, and the badge is
  // how the customer learns it did.
  const infoQuery = useQuery({ queryKey: ['assistant-info'], queryFn: () => assistantApi.info(),
    retry: false, refetchInterval: 60000 });
  const info = infoQuery.data?.data;
  const enabled = info?.enabled === true;
  const ratingEnabled = info?.ratingEnabled === true;

  const chatsQuery = useQuery({
    queryKey: ['assistant-chats'],
    queryFn: () => assistantApi.chats(),
    enabled,
    retry: false,
    // A human answer can land while the page is open; the list is how it becomes visible.
    refetchInterval: 60000,
  });

  const loadTranscript = async (id) => {
    const res = await assistantApi.chatMessages(id);
    setTranscript(res.data.map((m) => ({
      id: m.id, role: m.role, content: m.content, images: m.images || [], fromSupport: m.fromSupport === true,
      resolved: m.resolved, rating: m.rating,
    })));
  };

  const sendMutation = useMutation({
    mutationFn: ({ message, images }) => assistantApi.chat({
      chatId: chatId || undefined,
      message, images,
      slot: searchParams.get('slot') || undefined,
      goal: searchParams.get('goal') || undefined,
    }),
    onSuccess: async (res, vars) => {
      const d = res.data;
      const sentFrom = pendingForRef.current;
      pendingForRef.current = null;
      queryClient.invalidateQueries({ queryKey: ['assistant-chats'] });
      queryClient.invalidateQueries({ queryKey: ['assistant-info'] });
      // Decide against the ref, never inside a setState updater: React state updaters must be
      // pure, and StrictMode double-invokes them - side effects there duplicated every message
      // in the transcript (review finding).
      const stillHere = (sentFrom === 'new' && chatIdRef.current === null)
        || chatIdRef.current === sentFrom;
      if (!stillHere) {
        // The user opened another conversation while this one was thinking - the answer is
        // saved server-side in its own chat; do not splice it into the wrong transcript. Nor
        // offer its card here: a proposal belongs to the conversation that asked for it, and
        // switching away already cleared whatever was on screen.
        toast.info('Answer saved to your previous conversation.');
        return;
      }
      setChatId(d.chatId);
      // At most one of the two arrives, and a reply with neither clears the previous offer -
      // a card must never outlive the answer that made it.
      setProposal(d.proposal || null);
      setCopy(d.copy || null);
      // Optimistic splice first (no round trip), then reload for the message ids the rating
      // widget needs - and any human answer that may have arrived meanwhile.
      setTranscript((t) => [...t, { role: 'user', content: vars.message, images: vars.images },
        { role: 'assistant', content: d.reply }]);
      if (storeKey) {
        try { localStorage.setItem(storeKey, String(d.chatId)); } catch (_) {}
      }
      try { await loadTranscript(d.chatId); } catch (_) {}
    },
    onError: (e, vars) => {
      const sentFrom = pendingForRef.current;
      pendingForRef.current = null;
      // The typed question must survive a failed send (429/503/network) - restore it so
      // 'try again shortly' does not mean 'retype everything' (review finding).
      setDraft((cur) => (cur ? cur : vars.message));
      setImages((cur) => cur.length ? cur : vars.images || []);
      const why = e.response?.data?.error || e.message || 'Could not reach the assistant.';
      toast.error(why);
      /*
       * A toast is four seconds and then the transcript looks exactly as it did before the
       * question was asked - no question, no answer, no error - so a customer who looked away
       * cannot tell a failed send from one they never made. The bubble has always pushed a
       * persistent row for this; the page did not, and this is that row. It is deliberately not
       * given an id, so the rating controls skip it.
       */
      setTranscript((t) => [...t, { role: 'error', content: why }]);
      // Deep logging: 429 (daily limit), 503 (no model configured) and a gateway timeout all
      // arrive here and are indistinguishable in the UI. The status and the chat are what make
      // a report from a machine we cannot reach actionable.
      console.error('[assistant] send failed', {
        status: e.response?.status, chatId: sentFrom, message: why,
      });
    },
  });

  const reportMutation = useMutation({
    mutationFn: () => assistantApi.report(chatId),
    onSuccess: async (res) => {
      toast.success(`Reported to support as ticket ${res.data.ticketId} — a human will reply here.`);
      // Same reason as rating: the answer may already exist and have just been delivered.
      if (chatIdRef.current) {
        try { await loadTranscript(chatIdRef.current); } catch (_) {}
      }
      queryClient.invalidateQueries({ queryKey: ['assistant-chats'] });
      queryClient.invalidateQueries({ queryKey: ['assistant-info'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });

  const rateMutation = useMutation({
    mutationFn: ({ messageId, body }) => assistantApi.rate(messageId, body),
    onSuccess: async (_res, vars) => {
      // The server says {ok:true} and nothing else, by design. Reflect the verdict locally, then
      // reload: a "no" can be answered IMMEDIATELY from an already-approved answer, and without
      // this reload that reply sat unseen until the conversation was reopened (review).
      setTranscript((t) => t.map((m) => (m.id === vars.messageId
        ? { ...m, resolved: vars.body.resolved, rating: vars.body.rating ?? m.rating } : m)));
      queryClient.invalidateQueries({ queryKey: ['assistant-chats'] });
      queryClient.invalidateQueries({ queryKey: ['assistant-info'] });
      if (chatIdRef.current) {
        try { await loadTranscript(chatIdRef.current); } catch (_) {}
      }
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });

  const openChat = async (id, { silent = false } = {}) => {
    try {
      await loadTranscript(id);
      setChatId(id);
      // Cleared only once the switch has actually happened: a load that throws leaves the user
      // on the conversation they were already reading, and its offer is still theirs to accept.
      setProposal(null);
      setCopy(null);
      queryClient.invalidateQueries({ queryKey: ['assistant-chats'] });
      queryClient.invalidateQueries({ queryKey: ['assistant-info'] });
      if (storeKey) {
        try { localStorage.setItem(storeKey, String(id)); } catch (_) {}
      }
    } catch (e) {
      // A restore of a stale/foreign id must clear it, or the same toast fires on every mount
      // forever (review finding).
      if (silent) {
        if (storeKey) {
          try { localStorage.removeItem(storeKey); } catch (_) {}
        }
        return;
      }
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const newChat = () => {
    setChatId(null);
    setTranscript([]);
    setProposal(null);
    setCopy(null);
    if (storeKey) {
      try { localStorage.removeItem(storeKey); } catch (_) {}
    }
  };

  /* A card applied its change. The server may have written a note into the conversation, and the
   * transcript on screen was rendered before that existed - so re-read it, the same way rating and
   * reporting do. Silent on failure by the same rule they use: the card is already showing the
   * customer what happened, and a failed refresh must not turn that into an error. Read through the
   * ref, not `chatId`, because this fires from a callback that closed over an older render. */
  const refreshAfterApply = async () => {
    if (!chatIdRef.current) return;
    try { await loadTranscript(chatIdRef.current); } catch (_) {}
  };

  // Restore the last-open conversation after a reload - without this, every visit looked like
  // the history was gone and silently started a new server chat (review finding).
  useEffect(() => {
    if (!enabled || chatId !== null || !storeKey) return;
    let saved = null;
    try { saved = localStorage.getItem(storeKey); } catch (_) {}
    if (saved) openChat(Number(saved), { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, storeKey]);

  const send = () => {
    const message = draft.trim();
    if ((!message && !images.length) || sendMutation.isPending || !enabled) return;
    pendingForRef.current = chatId === null ? 'new' : chatId;
    setDraft('');
    setImages([]);
    sendMutation.mutate({ message, images });
  };

  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, sendMutation.isPending]);

  if (infoQuery.isError) {
    return (
      <div>
        <PageHeader icon={Skull} title={ALPHA_TITLE} description={DESCRIPTION} />
        <div className="aurora-card p-8 text-center sm:p-10" role="alert" data-testid="assistant-error">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
            <ShieldAlert className="h-6 w-6" />
          </span>
          <h3 className="mt-4">Assistant unavailable</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
            {infoQuery.error?.response?.data?.error || infoQuery.error?.message}
          </p>
          <Button variant="outline" onClick={() => infoQuery.refetch()} className="tap mt-5 rounded-full">
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
        </div>
      </div>
    );
  }

  const unread = info?.unreadSupportAnswers || 0;
  const chats = chatsQuery.data?.data || [];
  const currentChat = chats.find((c) => c.id === chatId);
  // First load of /info: nothing is known yet, so neither "disabled" nor an empty chat is honest.
  const booting = infoQuery.isPending;

  const listProps = {
    chats,
    chatId,
    busy: sendMutation.isPending,
  };

  return (
    <div
      className={cn('flex flex-col', embedded ? 'h-full min-h-0' : PAGE_HEIGHT)}
      data-testid="assistant-page"
    >
      <PageHeader
        icon={Skull}
        title={ALPHA_TITLE}
        description={DESCRIPTION}
        className="mb-4 shrink-0 sm:mb-5"
        actions={(
          <>
            <Button
              variant="outline" size="sm" className="tap relative rounded-full sm:hidden"
              onClick={() => setListOpen(true)}
              disabled={!enabled}
              data-testid="assistant-conversations-btn"
            >
              <MessagesSquare className="h-4 w-4" /> Conversations
              {unread > 0 && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-background" aria-hidden />}
            </Button>
            <Button
              size="sm" variant="outline" className="tap rounded-full"
              disabled={!chatId || reportMutation.isPending}
              onClick={() => reportMutation.mutate()}
              title="Files this conversation as a support ticket for a human"
            >
              <LifeBuoy className="h-4 w-4" /> Report to support
            </Button>
          </>
        )}
      />

      <div className="flex min-h-0 flex-1 gap-4">
        {/* conversations - frozen while a send is pending so a reply cannot land in the wrong view */}
        <aside className="aurora-card hidden w-44 shrink-0 flex-col gap-2 p-2 sm:flex md:w-64" aria-label="Conversations">
          <ConversationList {...listProps} onOpen={openChat} onNew={newChat} />
        </aside>

        {/* chat */}
        <Card className="aurora-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <CardContent className="flex min-h-0 flex-1 flex-col p-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/70 px-4 py-3">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/25">
                <Skull className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-heading text-base font-semibold">
                  {currentChat?.title || (chatId ? `conversation ${chatId}` : 'Undead Assistant')}
                </p>
                {info && (
                  <p className="text-xs text-muted-foreground tnum">
                    {info.usedToday}/{info.dailyLimit} today
                  </p>
                )}
              </div>
              {unread > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-1 text-xs font-medium text-success ring-1 ring-success/25"
                      data-testid="unread-support-answers">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
                  {unread} answer{unread === 1 ? '' : 's'} from support
                </span>
              )}
            </div>

            {info && !enabled && (
              <div className="mx-4 mt-4 flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm text-warning">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>The assistant isn't available on your account yet.</span>
              </div>
            )}
            {info && enabled && info.configured === false && (
              <div className="mx-4 mt-4 flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm text-warning">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>The assistant is enabled but no model is configured on the server yet.</span>
              </div>
            )}

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="assistant-transcript">
              {booting && (
                <div className="space-y-3" aria-busy="true" aria-label="Loading assistant">
                  <div className="h-14 w-3/5 animate-pulse rounded-xl bg-secondary/60" />
                  <div className="ml-auto h-10 w-2/5 animate-pulse rounded-xl bg-secondary/60" />
                  <div className="h-20 w-4/5 animate-pulse rounded-xl bg-secondary/60" />
                </div>
              )}
              {transcript.length === 0 && enabled && (
                <div className="aurora-card aurora-card-dashed mx-auto max-w-lg p-6 text-center sm:p-8" data-testid="assistant-empty">
                  <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
                    <Skull className="h-5 w-5" />
                  </span>
                  <h3 className="mt-3">Ask about your slots and settings</h3>
                  <p className="mt-1 text-sm text-muted-foreground text-pretty">
                    For example{' '}
                    <i>“why is slot4 not reposting reels?”</i> or <i>“what does setting 616 do?”</i>.
                    Answers come from the product's own knowledge plus your account's real state.
                    The assistant explains, and never changes a setting without showing you the
                    change first and waiting for you to apply it.
                    {ratingEnabled && <> Tell it whether an answer resolved your question — a “No” goes to a human, who replies right here.</>}
                  </p>
                </div>
              )}
              {transcript.map((m, i) => (
                <div key={m.id || `tmp-${i}`} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                  <div className={cn('min-w-0 max-w-[85%]', m.role === 'user' ? '' : 'min-w-[40%]')}>
                    {m.fromSupport && (
                      <div className="mb-1 flex items-center gap-1 text-xs font-medium text-success">
                        <UserCheck className="h-3.5 w-3.5" /> Support team
                      </div>
                    )}
                    <div className={cn(
                      // RichText now emits real blocks, so pre-wrap would double every gap it already made.
                      'rounded-2xl px-4 py-2.5 text-sm break-words',
                      m.role === 'user' ? 'rounded-br-md bg-primary/15 text-foreground ring-1 ring-primary/20'
                        // A send that failed, pushed by the mutation's onError. It reads as a
                        // failure and stays there until the next successful send reloads the
                        // transcript over it - which is the point: it outlives its toast.
                        : m.role === 'error' ? 'rounded-bl-md bg-destructive/15 text-destructive'
                          : m.fromSupport ? 'rounded-bl-md border border-success/40 bg-success/10 text-foreground'
                            : 'rounded-bl-md bg-secondary/60 text-foreground'
                    )}>
                      {m.role === 'user' ? <div className="whitespace-pre-wrap break-words">{m.content}</div> : <RichText text={m.content} />}
                  <AssistantAttachments images={m.images} />
                    </div>
                    {ratingEnabled && m.role === 'assistant' && m.id && (
                      <RatingWidget
                        message={m}
                        busy={rateMutation.isPending}
                        onRate={(messageId, body) => rateMutation.mutate({ messageId, body })}
                      />
                    )}
                  </div>
                </div>
              ))}
              {/* The offer the last answer made, under it and above the composer. Hidden while the
                * next question is in flight - it belongs to the answer above it, and leaving it
                * sitting there under a newer one invites applying a change to the wrong reply.
                * Hidden, not dropped: a send that fails leaves the offer where it was. Laid out
                * on the assistant's side of the transcript, at the same width as its messages,
                * because that is where the answer proposing it just appeared. */}
              {proposal && !sendMutation.isPending && (
                <div className="flex justify-start">
                  <div className="min-w-0 max-w-[85%] flex-1">
                    <ProposalCard
                      proposal={proposal}
                      onDone={refreshAfterApply}
                      onDismiss={() => setProposal(null)}
                    />
                  </div>
                </div>
              )}
              {copy && !sendMutation.isPending && (
                <div className="flex justify-start">
                  <div className="min-w-0 max-w-[85%] flex-1">
                    <CopyProposalCard
                      copy={copy}
                      onDone={refreshAfterApply}
                      onDismiss={() => setCopy(null)}
                    />
                  </div>
                </div>
              )}
              {sendMutation.isPending && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> thinking…
                </div>
              )}
              <div ref={bottomRef} />
            </div>

<AssistantComposer value={draft} onChange={setDraft} images={images} onImagesChange={setImages} onSend={send} disabled={!enabled} busy={sendMutation.isPending} />
          </CardContent>
        </Card>
      </div>

      {/* phone: the conversation list as a sheet (the side panel is hidden below sm) */}
      <Sheet open={listOpen} onOpenChange={setListOpen}>
        <SheetContent side="left" className="flex w-[86vw] max-w-sm flex-col gap-3 p-4">
          <SheetHeader className="space-y-0 pr-8 text-left">
            <SheetTitle className="font-heading text-base font-semibold">Conversations</SheetTitle>
          </SheetHeader>
          <ConversationList
            {...listProps}
            onOpen={(id) => { setListOpen(false); openChat(id); }}
            onNew={() => { setListOpen(false); newChat(); }}
            mobile
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
